import React from 'react';
import {Input, InputNumber, DatePicker, TimePicker} from 'antd';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import {AutosaveFieldOptions} from './types';

/** Controls that emit values while typing, rather than committed selections. */
export function isTypingControl(control: React.ReactElement) {
    const type = control.type;
    return (
        [Input, Input.TextArea, Input.Search, Input.Password, InputNumber].some(
            (component) => type === component
        ) ||
        type === 'textarea' ||
        (type === 'input' && !['checkbox', 'radio', 'range', 'file'].includes(control.props.type))
    );
}

/** Event inspection supports custom text inputs without guessing from string-valued selections. */
export function changeMode(
    control: React.ReactElement,
    option: AutosaveFieldOptions['autosave'],
    event: unknown
): 'typing' | 'immediate' {
    if (option === 'typing' || option === 'immediate') {
        return option;
    }
    if (isTypingControl(control)) {
        return 'typing';
    }

    const target = (event as {target?: {tagName?: string; type?: string}} | null)?.target;
    return target &&
        ['INPUT', 'TEXTAREA'].includes(target.tagName || '') &&
        !['checkbox', 'radio', 'range', 'file'].includes(target.type || '')
        ? 'typing'
        : 'immediate';
}

/** Compares committed values, ignoring picker internals and a select's display-only label. */
export function controlValuesEqual(control: React.ReactElement, left: unknown, right: unknown) {
    const normalize = (value: unknown): unknown => {
        if (moment.isMoment(value)) {
            if (control.type === TimePicker) {
                return value.format(control.props.format || 'HH:mm:ss');
            }
            if (control.type === DatePicker && !control.props.showTime) {
                return value.format('YYYY-MM-DD');
            }
            return value.valueOf();
        }
        if (Array.isArray(value)) {
            return value.map(normalize);
        }
        if (control.props.labelInValue && value && typeof value === 'object') {
            return (value as {key?: string}).key;
        }
        return value;
    };
    return isEqual(normalize(left), normalize(right));
}
